import type { ReactNode } from 'react';

/**
 * Turns an error message into something a person can act on.
 *
 * The messages coming back are accurate but written in Acumatica's language:
 * "Acumatica rejected the API session: API Login Limit." is precise and tells
 * a customer nothing about what happened or what to do next. This maps the
 * conditions we actually see onto a plain title, an explanation, and whether
 * trying again is worth it.
 *
 * Deliberately matched on the message text rather than an error code. The
 * codes would be better and are worth adding to the API, but the strings are
 * ours, they are tested against a real instance, and an unrecognised message
 * degrades to showing exactly what it says today - never to something less
 * informative than before.
 */
interface Explained {
  title: string;
  advice: ReactNode;
  retryable: boolean;
}

function explain(message: string): Explained | null {
  const text = message.toLowerCase();

  if (text.includes('api login limit')) {
    return {
      title: 'Acumatica is at its connection limit',
      advice: (
        <>
          Your Acumatica license allows a limited number of simultaneous API
          connections, and they are all in use right now. Sessions are released
          automatically after a few minutes of inactivity, so this usually
          clears on its own. Signing out of unused Acumatica browser sessions
          frees one immediately.
        </>
      ),
      retryable: true,
    };
  }

  if (text.includes('insufficient_scope') || text.includes('403')) {
    return {
      title: 'Acumatica refused the request',
      advice: (
        <>
          The connected application may not have all the permissions AcuTidy
          needs. Re-authorizing from the instance page usually resolves it.
        </>
      ),
      retryable: true,
    };
  }

  if (text.includes('re-authoriz') || text.includes('re-authoris') || text.includes('invalid_grant')) {
    return {
      title: 'The Acumatica connection needs re-authorizing',
      advice: (
        <>
          The saved authorization is no longer valid. Open the instance page and
          choose <strong>Re-authorize in Acumatica</strong>.
        </>
      ),
      retryable: false,
    };
  }

  if (text.includes('not connected')) {
    return {
      title: 'This instance is not connected yet',
      advice: <>Finish the connection on the instance page before loading data.</>,
      retryable: false,
    };
  }

  if (text.includes('could not reach')) {
    return {
      title: 'AcuTidy could not reach your Acumatica instance',
      advice: (
        <>
          The instance may be offline, or its address may have changed. Check the
          instance URL on the connection page.
        </>
      ),
      retryable: true,
    };
  }

  return null;
}

export function ErrorNotice({
  message,
  onRetry,
}: {
  message: string | null | undefined;
  /** Rendered as a Try again button when the condition is worth retrying. */
  onRetry?: () => void;
}) {
  if (!message) return null;
  const explained = explain(message);

  return (
    <div className="notice error" role="alert">
      {explained ? (
        <>
          <strong className="notice-title">{explained.title}</strong>
          <p className="notice-body">{explained.advice}</p>
          {/* The raw message stays, quieter. It is what makes a support
              conversation possible, and hiding it would trade one person's
              tidy screen for another person's unsolvable ticket. */}
          <p className="notice-detail">{message}</p>
          {explained.retryable && onRetry && (
            <button type="button" onClick={onRetry}>
              Try again
            </button>
          )}
        </>
      ) : (
        message
      )}
    </div>
  );
}
